import AgeRangeSelector from "@/components/filters/AgeRangeSelector";
import DistanceSelector from "@/components/filters/DistanceSelector";
import HeightRangeSelector from "@/components/filters/HeightRangeSelector";
import { NationalityEthnicityFilter } from "@/components/filters/NationalityEthnicityFilter";
import BadgeSelector from "@/components/onboarding/data-form/BadgeSelector";
import LanguageSelector from "@/components/onboarding/data-form/LanguageSelector";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

import {
  CalendarHeartIcon,
  EraserIcon,
  RadiusIcon,
  RulerIcon,
  SearchIcon,
  SpeechIcon,
} from "lucide-react";

import SpinnerSmall from "@/components/SpinnerSmall";
import pinWhite from "../../assets/pin-white.svg";

import {
  DrinkingStatus,
  OpenTo,
  ProfileSearchFilters,
  SearchSortBy,
  SmokerStatus,
  TattooStatus,
} from "@/generated-api";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useRecentSearch, useSearchProfiles } from "@/tanstack-hooks";
import { useEffect } from "react";
import { Button } from "../ui/button";
import { useTranslation } from "@/hooks/useTranslation";
import {
  DEFAULT_FORM_VALUES,
  mapRecentSearchToFormValues,
  searchFormSchema,
  SearchFormSchema,
  searchFormToFilters,
} from "./filterUtils";
import CitySelector from "../onboarding/data-form/CitySelector";
import vape from "@/assets/vape.png";
import tattoo from "@/assets/tattoo-device.png";
import cocktail from "@/assets/cocktail.png";
import { OPEN_TO_EMOJI_MAP } from "../onboarding/data-form/openToEmojiMap";

export type SearchFiltersProps = {
  sorting: SearchSortBy;
  setSorting: (s: SearchSortBy) => void;
  setFiltersModalOpen?: (b: boolean) => void;
  search: ReturnType<typeof useSearchProfiles>["search"];
  isLoading: boolean;
  currentSearchParams: ProfileSearchFilters | null;
  onSearchTriggeredByParent: (fn: (s: SearchSortBy) => void) => void;
};

export default function SearchFilters({
  sorting,
  setSorting,
  setFiltersModalOpen,
  search,
  isLoading,
  currentSearchParams,
  onSearchTriggeredByParent,
}: SearchFiltersProps) {
  const t = useTranslation();
  const recentSearchQuery = useRecentSearch();
  const searchForm = useForm<SearchFormSchema>({
    resolver: zodResolver(searchFormSchema),
    defaultValues: DEFAULT_FORM_VALUES,
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });

  useEffect(() => {
    if (recentSearchQuery.isSuccess && !recentSearchQuery.data) {
      if (!currentSearchParams) {
        doSearch(searchForm.getValues(), sorting);
      }
    } else if (!recentSearchQuery.isLoading && recentSearchQuery.data) {
      setSorting(recentSearchQuery.data.sort_by);
      const formValues = mapRecentSearchToFormValues(recentSearchQuery.data);
      searchForm.reset(formValues);

      // Perform search if we don't have any current search params
      if (!currentSearchParams) {
        doSearch(searchForm.getValues(), recentSearchQuery.data.sort_by);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recentSearchQuery.data]);

  useEffect(() => {
    if (onSearchTriggeredByParent) {
      onSearchTriggeredByParent((s) => {
        const values = searchForm.getValues();
        doSearch(values, s);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function doSearch(values: SearchFormSchema, sortBy: SearchSortBy = sorting) {
    search(searchFormToFilters(values, sortBy));
    setFiltersModalOpen?.(false); // Close modal after search on mobile
  }

  function handleSubmitSearch(values: SearchFormSchema) {
    doSearch(values, sorting);
  }

  return (
    <Form {...searchForm}>
      <form
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onSubmit={searchForm.handleSubmit(handleSubmitSearch)}
        className="px-6"
      >
        <div className="flex flex-col gap-5">
          <FormField
            control={searchForm.control}
            name="city"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex flex-row">
                  <img src={pinWhite} className="w-4 h-4 mr-2" />
                  {t.SEARCH_PAGE.LABELS.LOCATION}
                </FormLabel>
                <FormControl>
                  <CitySelector
                    formField={field}
                    maxSelected={3}
                    placeholder={t.SEARCH_PAGE.PLACEHOLDERS.CITIES_INPUT}
                  />
                </FormControl>
                <FormMessage className="mt-1" />
              </FormItem>
            )}
          />
          {(searchForm.watch("city")?.length ?? 0) == 1 && (
            <FormField
              control={searchForm.control}
              name="distanceKm"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex flex-row">
                    <RadiusIcon className="h-4 w-4 mr-2" />
                    {t.SEARCH_PAGE.LABELS.DISTANCE}
                  </FormLabel>
                  <FormControl>
                    <DistanceSelector
                      value={field.value}
                      onChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage className="mt-1" />
                </FormItem>
              )}
            />
          )}
          <FormField
            control={searchForm.control}
            name="age"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex flex-row">
                  <CalendarHeartIcon className="h-4 w-4 mr-2" />
                  {t.SEARCH_PAGE.LABELS.AGE}
                </FormLabel>
                <FormControl>
                  <AgeRangeSelector
                    value={field.value}
                    onChange={field.onChange}
                  />
                </FormControl>
                <FormMessage className="mt-1" />
              </FormItem>
            )}
          />
          <FormField
            control={searchForm.control}
            name="languages"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex flex-row">
                  <SpeechIcon className="h-4 w-4 mr-2" />
                  {t.SEARCH_PAGE.LABELS.LANGUAGES}
                </FormLabel>
                <FormControl>
                  <LanguageSelector
                    formField={field}
                    placeholder={t.SEARCH_PAGE.PLACEHOLDERS.LANGUAGES_INPUT}
                  />
                </FormControl>
                <FormMessage className="mt-1" />
              </FormItem>
            )}
          />
          <NationalityEthnicityFilter form={searchForm} />
          <FormField
            control={searchForm.control}
            name="height"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex flex-row">
                  <RulerIcon className="h-4 w-4 mr-2" />
                  {t.SEARCH_PAGE.LABELS.HEIGHT}
                </FormLabel>
                <FormControl>
                  <HeightRangeSelector
                    value={field.value}
                    onChange={field.onChange}
                  />
                </FormControl>
                <FormMessage className="mt-1" />
              </FormItem>
            )}
          />
          <FormField
            control={searchForm.control}
            name="openTo"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex flex-row">
                  {t.SEARCH_PAGE.LABELS.OPEN_TO}
                </FormLabel>
                <div>
                  <FormControl>
                    <BadgeSelector
                      selectedValues={field.value || []}
                      onChange={field.onChange}
                      enumObject={OpenTo}
                      maxSelections={3}
                      valueToImageMap={OPEN_TO_EMOJI_MAP}
                    />
                  </FormControl>
                  <FormMessage className="mt-1" />
                </div>
              </FormItem>
            )}
          />
          <FormField
            control={searchForm.control}
            name="smoker"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex flex-row items-center gap-2">
                  <img src={vape} className="w-6 h-6" />
                  {t.SEARCH_PAGE.LABELS.SMOKER}
                </FormLabel>
                <FormControl>
                  <BadgeSelector
                    selectedValues={field.value || []}
                    onChange={field.onChange}
                    enumObject={SmokerStatus}
                    maxSelections={3}
                  />
                </FormControl>
                <FormMessage className="mt-1" />
              </FormItem>
            )}
          />
          <FormField
            control={searchForm.control}
            name="tattoos"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex flex-row items-center gap-2">
                  <img src={tattoo} className="w-6 h-6" />
                  {t.SEARCH_PAGE.LABELS.TATTOOS}
                </FormLabel>
                <FormControl>
                  <BadgeSelector
                    selectedValues={field.value || []}
                    onChange={field.onChange}
                    enumObject={TattooStatus}
                    maxSelections={3}
                  />
                </FormControl>
                <FormMessage className="mt-1" />
              </FormItem>
            )}
          />
          <FormField
            control={searchForm.control}
            name="drinking"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex flex-row items-center gap-2">
                  <img src={cocktail} className="w-5 h-5" />
                  {t.SEARCH_PAGE.LABELS.DRINKING}
                </FormLabel>
                <FormControl>
                  <BadgeSelector
                    selectedValues={field.value || []}
                    onChange={field.onChange}
                    enumObject={DrinkingStatus}
                    maxSelections={3}
                  />
                </FormControl>
                <FormMessage className="mt-1" />
              </FormItem>
            )}
          />
          <div className="flex flex-row items-center justify-end">
            <Button
              variant="secondary"
              className="flex rounded-full items-center gap-3"
              type="button"
              onClick={() => {
                searchForm.reset({
                  ...DEFAULT_FORM_VALUES,
                  city: [],
                  languages: [],
                  nationalities: [],
                  ethnicity: undefined,
                  openTo: [],
                  tattoos: [],
                  smoker: [],
                  drinking: [],
                });
              }}
            >
              <EraserIcon className="h-5 w-5" />
              {t.SEARCH_PAGE.LABELS.CLEAR_BUTTON}
            </Button>
          </div>
        </div>
        <div className="sticky flex bg-background pb-6 pt-4 mt-2 bottom-0">
          <Button
            type="submit"
            className="w-full rounded-full"
            disabled={isLoading}
          >
            {isLoading ? (
              <SpinnerSmall black={true} />
            ) : (
              <>
                <SearchIcon className="w-4 h-4 mr-2" strokeWidth={3} />
                {t.SEARCH_PAGE.LABELS.SEARCH_BUTTON}
              </>
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
}
